import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-md/lib/Buttons';
import Select from 'react-select';
import { MSpinner } from 'components/Shared';
import { Card, CardTitle, CardText, Grid, Cell } from 'react-md';
import { getEmployeesByRoles } from "requests/employee";
import { ReactLargeSelectClassName } from "components/Shared/MSelect";

// This component is intended to be used to assign regions to different employee roles. This is accomplished by
// assigning different props to the 'assignStates' and 'assignCounties' functions, as well as specifying which
// roles should show up in the employee list.

class RegionAssignment extends React.Component {
    constructor(props) {
        super(props);
        this.handleStatesSelectChange = this.handleStatesSelectChange.bind(this);
        this.handleCountiesSelectChange = this.handleCountiesSelectChange.bind(this);
        this.handleManagerSelectChange = this.handleManagerSelectChange.bind(this);
        this.submit = this.submit.bind(this);
        this.clear = this.clear.bind(this);
        this.state = {
            statesValue: [],
            countiesValue: [],
            managerValue: {},
            isFetchingEmployeeList: false,
            employeeList: []
        };
        this.mounted = { current: true };
        this.fetchEmployees = this.fetchEmployees.bind(this);
    }

    componentDidMount() {
        this.props.getStates();
    }

    handleStatesSelectChange(value) {
        if (value.length && !this.props.disableCountySelect) {
            this.props.getCountiesByState(value.map(x => x.value));
        }
        this.setState({ statesValue: value, isFetchingEmployeeList: true });
        this.validateCountiesValue(value);
        this.fetchEmployees();
    }

    handleCountiesSelectChange(value) {
        this.setState({ countiesValue: value });
    }

    handleManagerSelectChange(value) {
        this.setState({ managerValue: value });
    }

    fetchEmployees() {
        getEmployeesByRoles(this.props.employeeRoles, data => this.setState({ isFetchingEmployeeList: false, employeeList: data }), this.mounted);
    }

    validateCountiesValue(value) {
        let validStateList = value.map(x => x.value);
        let validCountiesList = this.state.countiesValue.filter(x => validStateList.includes(x.stateName));
        this.setState({ countiesValue: validCountiesList });
    }

    submit() {
        // first assign counties if any
        let countiesFIPSList = this.state.countiesValue.map(x => x.fips);
        if (Array.isArray(countiesFIPSList) && countiesFIPSList.length) {
            let assignCountiesObject = {
                employeeId: this.state.managerValue.id,
                countyFIPSList: countiesFIPSList
            };
            this.props.assignCounties(assignCountiesObject);
        }
        // only keep those states with no county selected
        let statesOfCounties = this.state.countiesValue.map(x => x.stateName);
        let statesWithNoCountySelected = this.state.statesValue.filter(x => !statesOfCounties.includes(x.value));

        if (Array.isArray(statesWithNoCountySelected) && statesWithNoCountySelected.length) {
            let assignStatesObject = {
                employeeId: this.state.managerValue.id,
                stateAbbreviationList: statesWithNoCountySelected.map(x => x.value)
            };
            this.props.assignStates(assignStatesObject);
        }
        this.clear();
    }

    clear() {
        this.setState({ statesValue: [], countiesValue: [], managerValue: {} });
    }

    render() {
        if (this.props.isFetchingStates || this.props.isFetchingCountiesByState || this.state.isFetchingEmployeeList) {
            return <MSpinner />;
        }

        return (
            <Grid>
                <Cell size={3}>
                    <Card className="card-unnested">
                        <CardTitle
                            title={this.props.cardTitle}
                        />
                        <CardText>
                            <Select
                                className={ReactLargeSelectClassName}
                                isMulti={true}
                                onChange={this.handleStatesSelectChange}
                                options={this.props.states.map(item => {
                                    return { label: item.abbreviationAndName, value: item.abbreviation };
                                })
                                }
                                placeholder="Select States"
                                simpleValue
                                value={this.state.statesValue}
                                removeSelected={true}
                                classNamePrefix="react-select"

                            />
                            <br />
                            {this.props.disableCountySelect ||
                                (<>
                                    <Select
                                        className={ReactLargeSelectClassName}
                                        isMulti={true}
                                        isDisabled={!this.state.statesValue.length}
                                        onChange={this.handleCountiesSelectChange}
                                        options={this.props.counties.map(item => {
                                            return { label: item.state + " - " + item.name, value: item.name, stateName: item.state, fips: item.fips };
                                        })
                                        }
                                        placeholder="Select Counties"
                                        removeSelected={true}
                                        simpleValue
                                        value={this.state.countiesValue}
                                        classNamePrefix="react-select"
                                    />
                                    <br />
                                </>)
                            }
                            <Select
                                className={ReactLargeSelectClassName}
                                isMulti={false}
                                isDisabled={!this.state.statesValue.length}
                                onChange={this.handleManagerSelectChange}
                                options={this.state.employeeList.map(item => {
                                    return { label: item.firstName + " " + item.lastName, value: item.firstName + " " + item.lastName, id: item.employeeId };
                                })}
                                placeholder="Select Manager"
                                simpleValue
                                isSearchable={true}
                                classNamePrefix="react-select"
                            />
                            <Button
                                primary
                                raised
                                children="Submit"
                                className="full-width m-t-10"
                                disabled={!this.state.statesValue.length || Object.keys(this.state.managerValue).length === 0}
                                onClick={this.submit}
                            />
                        </CardText>
                    </Card>
                </Cell>
            </Grid>
        );
    }
}

RegionAssignment.propTypes = {
    // standard props
    states: PropTypes.array.isRequired,                     // list of states (redux)
    counties: PropTypes.array.isRequired,                   // list of counties by selected states (redux)
    isFetchingStates: PropTypes.bool.isRequired,            // (redux)
    isFetchingCountiesByState: PropTypes.bool.isRequired,   // (redux)
    getStates: PropTypes.func.isRequired,                   // function to get a list of states (redux)
    getCountiesByState: PropTypes.func,                     // function to get a list of counties by state (redux)
    
    // override props
    assignStates: PropTypes.func.isRequired,                // function to assign states to employee (redux)
    assignCounties: PropTypes.func,                         // function to assign counties to employee (redux)
    cardTitle: PropTypes.string.isRequired,                 // title of the card (static data)
    employeeRoles: PropTypes.array.isRequired,              // which roles should populate in the employee drop down (static data)
    disableCountySelect: PropTypes.bool,                    // if true, this component only selects states
};

export default RegionAssignment;